/**
 * @typedef {'FSL'|'Warehouse'|'On-Site'|'Supplier'} OriginType
 * @typedef {'Active'|'In Transit'|'Delivered'} ShipmentStatus
 *
 * @typedef {Object} Shipment
 * @property {string} id               - GUID primary key
 * @property {string} shipmentNumber   - display-only (e.g. "SHP-30009")
 * @property {OriginType} originType
 * @property {string} originLocation
 * @property {string} customerName
 * @property {string} customerAddress
 * @property {string} carrier
 * @property {string} trackingNumber
 * @property {ShipmentStatus} status
 * @property {string} createdDate      - ISO date string
 * @property {string|null} partOrderId     - GUID FK → partOrders.id
 * @property {string|null} ticketNumber    - display-only (e.g. "INC10092")
 * @property {string|null} serialNumber    - serial number of the shipped unit (null for Supplier or non-serialized parts)
 */

/** @type {Shipment[]} */
export const shipments = [
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000001',
    shipmentNumber: 'SHP-30009',
    originType: 'Warehouse',
    originLocation: 'Central Warehouse — Dallas, TX',
    customerName: 'Ashford Capital Partners',
    customerAddress: '88 Wall St, New York, NY 10005',
    carrier: 'FedEx',
    trackingNumber: '7489234710294857',
    status: 'Delivered',
    createdDate: '2026-03-13',
    partOrderId: 'c1a2b3d4-0001-4000-8001-100000000006',
    ticketNumber: 'INC10092',
    serialNumber: 'SN-HBA-048',
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000002',
    shipmentNumber: 'SHP-30011',
    originType: 'FSL',
    originLocation: 'FSL Atlanta — Doraville, GA',
    customerName: 'Harborview Medical Center',
    customerAddress: '400 Medical Center Dr, Atlanta, GA 30301',
    carrier: 'UPS',
    trackingNumber: '1Z999AA10123456784',
    status: 'Delivered',
    createdDate: '2026-03-17',
    partOrderId: 'c1a2b3d4-0001-4000-8001-100000000001',
    ticketNumber: 'INC10041',
    serialNumber: 'SN-PWR-003',
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000003',
    shipmentNumber: 'SHP-30014',
    originType: 'Warehouse',
    originLocation: 'Central Warehouse — Dallas, TX',
    customerName: 'Coastal Capital Group',
    customerAddress: '200 Bay St, Toronto, ON M5J 2W7',
    carrier: 'FedEx',
    trackingNumber: '7489234710295001',
    status: 'In Transit',
    createdDate: '2026-03-21',
    partOrderId: 'c1a2b3d4-0001-4000-8001-100000000002',
    ticketNumber: 'INC10052',
    serialNumber: 'SN-NIC-034',
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000004',
    shipmentNumber: 'SHP-30017',
    originType: 'FSL',
    originLocation: 'FSL Chicago — Elk Grove Village, IL',
    customerName: 'Greenfield Hospital Trust',
    customerAddress: '1 Hospital Drive, Chicago, IL 60601',
    carrier: 'UPS',
    trackingNumber: '1Z999AA10123456812',
    status: 'Active',
    createdDate: '2026-03-22',
    partOrderId: 'c1a2b3d4-0001-4000-8001-100000000005',
    ticketNumber: 'INC10085',
    serialNumber: 'SN-FAN-001',
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000005',
    shipmentNumber: 'SHP-30019',
    originType: 'FSL',
    originLocation: 'FSL Los Angeles — Torrance, CA',
    customerName: 'BlueStar Semiconductors',
    customerAddress: '3500 Harbor Blvd, Costa Mesa, CA 92626',
    carrier: 'DHL',
    trackingNumber: '1234567890',
    status: 'In Transit',
    createdDate: '2026-03-20',
    partOrderId: null,
    ticketNumber: null,
    serialNumber: null,
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000006',
    shipmentNumber: 'SHP-30021',
    originType: 'Warehouse',
    originLocation: 'East Coast Warehouse — Edison, NJ',
    customerName: 'Westfield City Council',
    customerAddress: '100 Municipal Plaza, Clearfield, PA 16830',
    carrier: 'FedEx',
    trackingNumber: '7489234710295088',
    status: 'Active',
    createdDate: '2026-03-23',
    partOrderId: 'c1a2b3d4-0001-4000-8001-100000000007',
    ticketNumber: 'INC10104',
    serialNumber: 'SN-PWR-015',
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000008',
    shipmentNumber: 'SHP-30026',
    originType: 'Supplier',
    originLocation: 'Bridgepoint Components',
    vendorName: 'Bridgepoint Components',
    customerName: 'BlueStar Semiconductors',
    customerAddress: '3500 Harbor Blvd, Costa Mesa, CA 92626',
    carrier: 'UPS',
    trackingNumber: '1Z999AA10123456900',
    status: 'Active',
    createdDate: '2026-03-25',
    partOrderId: null,
    ticketNumber: null,
    serialNumber: null,
  },
  {
    id: 'b2c3d4e5-0002-4000-8002-200000000009',
    shipmentNumber: 'SHP-30028',
    originType: 'Warehouse',
    originLocation: 'East Coast Warehouse — Edison, NJ',
    customerName: 'NovaStar Research Labs',
    customerAddress: '5000 Lakewood Ranch Blvd, Sarasota, FL 34211',
    carrier: 'FedEx',
    trackingNumber: '7489234710295214',
    status: 'In Transit',
    createdDate: '2026-03-27',
    partOrderId: 'c1a2b3d4-0001-4000-8001-100000000014',
    ticketNumber: 'INC10172',
    serialNumber: 'SN-N38-001',
  },
];

// ---------------------------------------------------------------------------

/**
 * @typedef {'Active'|'Pending'|'In Transit'|'Delivered'} VendorShipmentStatus
 *
 * @typedef {Object} VendorShipment
 * @property {string} id                   - GUID primary key
 * @property {string} shipmentNumber        - display-only (e.g. "RS8001")
 * @property {string} vendorName
 * @property {string} partNumber
 * @property {string} stockLocation         - matches partReplenishmentOrders[].stockLocation
 * @property {string} carrier
 * @property {string} trackingNumber
 * @property {VendorShipmentStatus} status
 * @property {string} estimatedDeliveryDate - ISO date string
 * @property {string} createdDate           - ISO date string
 * @property {string|null} partReplenishmentOrderId      - GUID FK → partReplenishmentOrders.id
 * @property {string|null} partReplenishmentOrderNumber  - display-only (e.g. "REP5001")
 */

/** @type {VendorShipment[]} */
export const vendorShipments = [
  {
    id: 'd3e4f5a6-0003-4000-8003-300000000001',
    shipmentNumber: 'RS8001',
    vendorName: 'Vantage Technology Supply',
    partNumber: 'RAM-32GB-DDR5',
    stockLocation: 'On-Site — IBM Logistics',
    carrier: 'FedEx',
    trackingNumber: 'TECH20260320001',
    status: 'In Transit',
    estimatedDeliveryDate: '2026-03-25',
    createdDate: '2026-03-20',
    partReplenishmentOrderId: 'e4f5a6b7-0004-4000-8004-400000000001',
    partReplenishmentOrderNumber: 'REP5001',
  },
  {
    id: 'd3e4f5a6-0003-4000-8003-300000000002',
    shipmentNumber: 'RS8002',
    vendorName: 'Crestline Distribution',
    partNumber: 'HBA-16G-FC',
    stockLocation: 'East Coast Warehouse',
    carrier: 'UPS',
    trackingNumber: 'ING20260310888',
    status: 'Delivered',
    estimatedDeliveryDate: '2026-03-15',
    createdDate: '2026-03-10',
    partReplenishmentOrderId: 'e4f5a6b7-0004-4000-8004-400000000002',
    partReplenishmentOrderNumber: 'REP5002',
  },
  {
    id: 'd3e4f5a6-0003-4000-8003-300000000003',
    shipmentNumber: 'RS8003',
    vendorName: 'Bridgepoint Components',
    partNumber: 'NIC-10G-RJ45',
    stockLocation: 'On-Site — Coastal Capital Group',
    carrier: 'DHL',
    trackingNumber: 'ARW20260321077',
    status: 'Pending',
    estimatedDeliveryDate: '2026-03-28',
    createdDate: '2026-03-21',
    partReplenishmentOrderId: 'e4f5a6b7-0004-4000-8004-400000000003',
    partReplenishmentOrderNumber: 'REP5003',
  },
  {
    id: 'd3e4f5a6-0003-4000-8003-300000000004',
    shipmentNumber: 'RS8004',
    vendorName: 'Pinnacle Technology Group',
    partNumber: 'PWR-1100-DC',
    stockLocation: 'FSL Atlanta',
    carrier: 'FedEx',
    trackingNumber: 'SYN20260305044',
    status: 'Delivered',
    estimatedDeliveryDate: '2026-03-12',
    createdDate: '2026-03-05',
    partReplenishmentOrderId: 'e4f5a6b7-0004-4000-8004-400000000005',
    partReplenishmentOrderNumber: 'REP5005',
  },
  {
    id: 'd3e4f5a6-0003-4000-8003-300000000005',
    shipmentNumber: 'RS8005',
    vendorName: 'Vantage Technology Supply',
    partNumber: 'SSD-3840-NVMe',
    stockLocation: 'Central Warehouse — Dallas',
    carrier: 'UPS',
    trackingNumber: 'TECH20260322002',
    status: 'Active',
    estimatedDeliveryDate: '2026-03-27',
    createdDate: '2026-03-22',
    partReplenishmentOrderId: 'e4f5a6b7-0004-4000-8004-400000000004',
    partReplenishmentOrderNumber: 'REP5004',
  },
];
